import { useState } from "react";
import type { InputProps } from "ra-core";
import { useInput, useResourceContext, FieldTitle } from "ra-core";
import {
  FormControl,
  FormError,
  FormField,
  FormLabel,
} from "@/components/admin/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputHelperText } from "@/components/admin/input-helper-text";
import { cn } from "@/lib/utils";
import { Eye, EyeOff } from "lucide-react";

export type PasswordInputProps = Omit<InputProps, "type"> & {
  inputClassName?: string;
} & Omit<React.ComponentProps<"input">, "type">;

/**
 * Password input with a show/hide toggle. Wraps the same FormField/Input
 * plumbing as `<TextInput>`, just fixed to type="password"/"text".
 */
export const PasswordInput = (props: PasswordInputProps) => {
  const resource = useResourceContext(props);
  const {
    label,
    source,
    className,
    inputClassName,
    helperText,
    validate: _validateProp,
    format: _formatProp,
    defaultValue,
    ...rest
  } = props;
  const { id, field, isRequired } = useInput(props);
  const [visible, setVisible] = useState(false);

  return (
    <FormField id={id} className={className} name={field.name}>
      {label !== false && (
        <FormLabel>
          <FieldTitle
            label={label}
            source={source}
            resource={resource}
            isRequired={isRequired}
          />
        </FormLabel>
      )}
      <FormControl>
        <div className="relative">
          <Input
            {...rest}
            {...field}
            type={visible ? "text" : "password"}
            className={cn("pr-10", inputClassName)}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            tabIndex={-1}
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? "Hide password" : "Show password"}
            className="absolute right-1 top-1/2 size-7 -translate-y-1/2 text-muted-foreground hover:bg-transparent hover:text-foreground"
          >
            {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </Button>
        </div>
      </FormControl>
      <InputHelperText helperText={helperText} />
      <FormError />
    </FormField>
  );
};
